import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, ArrowRight, Github, Linkedin, Send, RefreshCw, CheckCircle2, Clock, Globe, MessageCircle, Instagram } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';

export default function ContactForm() {
  const [name, setName] = useState<string>('');
  const [whatsapp, setWhatsapp] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [budget, setBudget] = useState<string>('$5,000 - $10,000');
  
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [lastWhatsappUrl, setLastWhatsappUrl] = useState<string>('');
  const [kyivTime, setKyivTime] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  // World timezone ticking clock for Kyiv, Ukraine (GMT+3 in May daylight savings)
  useEffect(() => {
    const updateTime = () => {
      const options: Intl.DateTimeFormatOptions = {
        timeZone: 'Europe/Kyiv',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      };
      const formatter = new Intl.DateTimeFormat('en-US', options);
      setKyivTime(formatter.format(new Date()));
    };

    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!name || !whatsapp || !message) {
      setFormError("Please fill out all fields before submitting.");
      return;
    }

    setIsSubmitting(true);
    
    // Construct the WhatsApp message brief format
    const formattedText = `Hello Jahangir! I have filled out a project inquiry brief on your portfolio:

*Client Name:* ${name}
*WhatsApp Number:* ${whatsapp}
*Estimated Budget:* ${budget}

*Project Details:*
${message}`;

    const whatsappUrl = `https://wa.me/923045886776?text=${encodeURIComponent(formattedText)}`;
    setLastWhatsappUrl(whatsappUrl);

    // Simulate transmission success and redirect
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      // Try using window.open, fallback to location.href if blocked by sandbox
      try {
        const opened = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        if (!opened) {
          window.location.href = whatsappUrl;
        }
      } catch (err) {
        window.location.href = whatsappUrl;
      }
      
      // Auto-reset form fields
      setName('');
      setWhatsapp('');
      setMessage('');
    }, 1500);
  };

  const socialLinks = [
    { label: 'LinkedIn', url: 'https://www.linkedin.com/in/jahangir420/', icon: <Linkedin className="w-4 h-4" /> },
    { label: 'Instagram', url: 'https://instagram.com/jahangir_khn', icon: <Instagram className="w-4 h-4" /> },
  ];

  return (
    <section id="contact" className="pt-24 pb-12 scroll-mt-24 relative overflow-hidden bg-transparent text-neutral-900 dark:text-white transition-colors duration-500">
      {/* Background Ambience / Blur Overlays replicating premium high-end portfolio layout */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-950/20 via-neutral-950/20 to-neutral-950 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -top-40 right-20 w-[450px] h-[450px] bg-emerald-500/5 blur-[130px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -bottom-40 left-10 w-[550px] h-[550px] bg-emerald-500/5 blur-[150px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 pb-20 border-b border-neutral-200/50 dark:border-neutral-800/20">
          
          {/* Left Column: CTA Pitch */}
          <motion.div 
            initial={isMobile ? { opacity: 1, x: 0, scale: 1 } : { opacity: 0, x: -75, scale: 0.94 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: false, amount: 0.1 }}
            transition={isMobile ? { duration: 0.2 } : { type: "spring", stiffness: 65, damping: 14 }}
            className="lg:col-span-5 space-y-8 flex flex-col justify-between"
          >
            <div className="space-y-6">
              <span className="font-mono text-[10px] tracking-[0.4em] text-emerald-500 font-extrabold uppercase block">
                COLLABORATION
              </span>
              <h2 className="text-4xl sm:text-6xl font-sans tracking-tight leading-none text-neutral-900 dark:text-white font-medium hover:text-emerald-500 transition-colors duration-300 cursor-default">
                Let's <span className="font-serif italic font-semibold text-neutral-500 dark:text-neutral-300 hover:text-emerald-400 transition-colors duration-300">make</span> <br />
                art together.
              </h2>
              <p className="text-sm sm:text-base text-neutral-500 dark:text-neutral-400 font-sans font-light leading-relaxed max-w-sm pt-2">
                Have an ambitious product launch, custom asset design, or strategic corporate campaign? Drop me a line, and let's craft an unparalleled digital landmark.
              </p>
            </div>

            {/* Quick Email & WhatsApp details */}
            <div className="space-y-3 pt-6 border-t border-neutral-100 dark:border-neutral-900 mt-6 md:mt-8">
              <p className="font-mono text-[9px] sm:text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                DIRECT INQUIRIES
              </p>
              <div className="flex flex-col gap-2.5">
                <a
                  href="mailto:connect@madebyjahangir.com"
                  className="group flex items-center gap-2.5 text-sm sm:text-base font-medium text-neutral-700 dark:text-neutral-200 hover:text-emerald-500 transition-colors"
                  data-cursor="hover"
                >
                  <div className="p-2 rounded-full bg-neutral-50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-800 group-hover:border-emerald-500 group-hover:bg-emerald-500/5 transition-all text-neutral-400 group-hover:text-emerald-500">
                    <Mail className="w-4 h-4" />
                  </div>
                  connect@madebyjahangir.com
                </a>
                <a
                  href="https://wa.me/923045886776"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2.5 text-sm sm:text-base font-medium text-neutral-700 dark:text-neutral-200 hover:text-emerald-500 transition-colors"
                  data-cursor="hover"
                >
                  <div className="p-2 rounded-full bg-green-500/10 dark:bg-green-500/15 border border-green-500/30 text-green-500 group-hover:bg-green-500 group-hover:text-white group-hover:border-green-600 transition-all">
                    <MessageCircle className="w-4 h-4 fill-green-500/20 group-hover:fill-white/20 transition-all" />
                  </div>
                  <span className="text-green-600 dark:text-green-400 font-semibold">+92 304-5886776</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Premium Contact Fields */}
          <motion.div 
            initial={isMobile ? { opacity: 1, x: 0, scale: 1 } : { opacity: 0, x: 75, scale: 0.94 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: false, amount: 0.1 }}
            transition={isMobile ? { duration: 0.2 } : { type: "spring", stiffness: 65, damping: 14, delay: 0.12 }}
            className="lg:col-span-7 bg-neutral-50 dark:bg-neutral-900/10 border border-neutral-150 dark:border-neutral-800/40 p-8 sm:p-12 rounded-3xl relative"
          >
            <AnimatePresence mode="wait">
              {!submitSuccess ? (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="space-y-8"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    {/* Name field */}
                    <div className="space-y-2">
                      <label className="font-mono text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">YOUR NAME</label>
                      <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Charlotte Vance"
                        className="w-full bg-transparent border-b border-neutral-200 dark:border-neutral-800 focus:border-emerald-500 focus:outline-none py-3 text-neutral-900 dark:text-white placeholder-neutral-300 dark:placeholder-neutral-700 font-sans transition-colors font-medium text-sm sm:text-base"
                      />
                    </div>

                    {/* WhatsApp field */}
                    <div className="space-y-2">
                      <label className="font-mono text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">WHATSAPP NUMBER</label>
                      <input
                        type="tel"
                        required
                        value={whatsapp}
                        onChange={(e) => setWhatsapp(e.target.value)}
                        placeholder="e.g. +92 304-5886776"
                        className="w-full bg-transparent border-b border-neutral-200 dark:border-neutral-800 focus:border-emerald-500 focus:outline-none py-3 text-neutral-900 dark:text-white placeholder-neutral-300 dark:placeholder-neutral-700 font-sans transition-colors font-medium text-sm sm:text-base"
                      />
                    </div>
                  </div>

                  {/* Message details */}
                  <div className="space-y-2">
                    <label className="font-mono text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">TELL ME ABOUT THE PROJECT</label>
                    <textarea
                      required
                      rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="e.g. We are building a climateVC platform and require institutional visual systems..."
                      className="w-full bg-transparent border-b border-neutral-200 dark:border-neutral-800 focus:border-emerald-500 focus:outline-none py-3 text-neutral-900 dark:text-white placeholder-neutral-300 dark:placeholder-neutral-700 font-sans transition-colors font-medium text-sm sm:text-base resize-none"
                    />
                  </div>

                  {/* Budget scales selector */}
                  <div className="space-y-4">
                    <label className="font-mono text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">ESTIMATED BUDGET</label>
                    <div className="flex flex-wrap gap-2.5">
                      {['<$5k', '$5,000 - $10,000', '$10,000 - $25,000', '$25,000+'].map((scope) => (
                        <button
                          key={scope}
                          type="button"
                          onClick={() => setBudget(scope)}
                          className={`font-mono text-[10px] sm:text-xs font-semibold px-4 py-2 rounded-full border transition-all ${
                            budget === scope
                              ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500 font-bold shadow shadow-emerald-500/5'
                              : 'border-neutral-200 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 hover:border-neutral-400 dark:hover:border-neutral-600'
                          }`}
                          data-cursor="hover"
                        >
                          {scope}
                        </button>
                      ))}
                    </div>
                  </div>

                  {formError && (
                    <div className="w-full text-center text-xs text-red-500 font-mono font-bold bg-red-500/10 dark:bg-red-500/5 border border-red-500/20 px-4 py-3 rounded-lg animate-pulse">
                      {formError}
                    </div>
                  )}

                  {/* Interactive state submit button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full group flex items-center justify-between gap-4 border-2 border-emerald-500 bg-emerald-500 hover:bg-transparent dark:hover:bg-transparent text-white hover:text-emerald-500 px-8 py-4 rounded-full font-mono text-xs font-bold tracking-widest uppercase transition-all duration-300 shadow shadow-emerald-500/10"
                    data-cursor="hover"
                  >
                    {isSubmitting ? (
                      <>
                        TRANSMITTING BRIEF...
                        <RefreshCw className="w-4 h-4 animate-spin text-white group-hover:text-emerald-500" />
                      </>
                    ) : (
                      <>
                        SEND INQUIRY BRIEF
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="h-[400px] flex flex-col items-center justify-center text-center space-y-6"
                >
                  <div className="p-4 rounded-full bg-green-500/10 border border-green-500/20 text-green-500">
                    <CheckCircle2 className="w-12 h-12" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-sans text-xl sm:text-2xl font-semibold tracking-tight text-neutral-900 dark:text-white">
                      Brief Processed!
                    </h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 font-sans max-w-sm">
                      Press the button below to send your structured inquiry brief directly to my WhatsApp number <span className="text-green-500 font-bold">+92 304-5886776</span>.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 w-full max-w-xs pt-2 items-center">
                    <a
                      href={lastWhatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full text-xs font-bold font-mono uppercase tracking-wider text-white bg-green-600 hover:bg-green-700 active:scale-95 transition-all duration-250 shadow-[0_0_15px_rgba(34,197,94,0.35)]"
                      data-cursor="hover"
                    >
                      <MessageCircle className="w-4 h-4 fill-white/10" />
                      Send via WhatsApp
                    </a>
                    <button
                      onClick={() => setSubmitSuccess(false)}
                      className="font-mono text-[10px] font-bold text-neutral-400 hover:text-emerald-500 hover:underline pt-2"
                      data-cursor="hover"
                    >
                      ← Back to Form
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Footer Area with World Time Clock and Social Links */}
        <div className="pt-12 flex flex-col sm:flex-row justify-between items-center gap-6">
          {/* Copyright humble metadata */}
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
            <span className="font-mono text-[10px] sm:text-xs text-neutral-400 dark:text-neutral-500">
              © 2026 MADE BY JAHANGIR. ALL RIGHTS RESERVED.
            </span>
          </div>

          {/* Kyoto/Kyiv real time world clock ticker */}
          <div className="flex items-center gap-2 border border-neutral-100 dark:border-neutral-900 bg-neutral-50/50 dark:bg-neutral-900/50 backdrop-blur px-4 py-1.5 rounded-full text-xs font-mono font-bold tracking-tight text-neutral-500 dark:text-neutral-400">
            <Globe className="w-3.5 h-3.5 text-emerald-500 animate-spin" style={{ animationDuration: '10s' }} />
            <span>KYIV, UA:</span>
            <span className="text-neutral-800 dark:text-neutral-200">{kyivTime || '08:24:00 PM'}</span>
            <span className="text-[10px] text-neutral-400">(GMT+3)</span>
          </div>

          {/* Social Links lists */}
          <div className="flex items-center gap-6">
            {socialLinks.map((item) => (
              <a
                key={item.label}
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs text-neutral-400 hover:text-emerald-500 transition-colors flex items-center gap-1.5"
                data-cursor="hover"
              >
                {item.icon}
                {item.label}
              </a>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
